import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Gift, Star, Award, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import useStore from '../store';

function PointsPage() {
  const { t } = useTranslation();
  const { user } = useStore();
  const [showReviewQueue, setShowReviewQueue] = useState(false);

  const rewards = [
    { id: 'premium_7d', titleKey: 'points.reward_premium_7d', descKey: 'points.reward_premium_7d_desc', points: 100, icon: Award },
    { id: 'extra_swap', titleKey: 'points.reward_extra_swap', descKey: 'points.reward_extra_swap_desc', points: 50, icon: TrendingUp },
    { id: 'menu_variant', titleKey: 'points.reward_menu_variant', descKey: 'points.reward_menu_variant_desc', points: 30, icon: Star }
  ];

  const handleRedeemReward = (reward) => {
    if ((user?.points_balance || 0) < reward.points) {
      toast.error(t('points.not_enough'));
      return;
    }
    toast.success(`${t(reward.titleKey)} (Demo)`);
  };

  return (
    <div className="py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center gap-3 mb-8">
          <div className="bg-secondary rounded-full p-3">
            <Gift className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-heading font-bold text-[#2C3E2D]" data-testid="points-title">
              {t('points.title')}
            </h1>
            <p className="text-gray-600">{t('points.subtitle')}</p>
          </div>
        </div>

        <Card className="mb-8 bg-gradient-to-r from-secondary to-primary text-white">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-lg mb-2">{t('points.your_points')}</p>
              <p className="text-6xl font-bold mb-4" data-testid="points-balance">
                {user?.points_balance || 0}
              </p>
              <p className="text-sm opacity-90">{t('points.collect_more')}</p>
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardHeader>
              <CardTitle>{t('points.earn_points')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">{t('points.write_review')}</h4>
                <p className="text-sm text-gray-600 mb-2">{t('points.review_desc')}</p>
                <div className="space-y-1 text-sm">
                  <p>&bull; {t('points.stars_text')}: <span className="font-bold text-success">+3 {t('common.points')}</span></p>
                  <p>&bull; {t('points.with_photo')}: <span className="font-bold text-success">+2 extra</span></p>
                  <p className="text-xs text-gray-500">{t('points.max_reviews')}</p>
                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-2">{t('points.upload_recipe')}</h4>
                <p className="text-sm text-gray-600 mb-2">{t('points.upload_desc')}</p>
                <div className="space-y-1 text-sm">
                  <p>&bull; {t('points.at_5th_review')}: <span className="font-bold text-success">+30 {t('common.points')}</span></p>
                  <p>&bull; {t('points.per_new_review')}: <span className="font-bold text-success">+1 tot +8</span></p>
                  <p className="text-xs text-gray-500">{t('points.depends_on_rating')}</p>
                </div>
              </div>

              <Button 
                className="w-full bg-primary hover:bg-primary-hover text-white"
                onClick={() => setShowReviewQueue(!showReviewQueue)}
                data-testid="review-recipes-btn"
              >
                <Star className="w-4 h-4 mr-2" />
                {showReviewQueue ? t('points.hide_queue') : t('points.review_recipes')}
              </Button>

              {showReviewQueue && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-gray-700">{t('points.queue_coming_soon')}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('points.recent_activity')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="text-center py-8 text-gray-500 text-sm">
                  {t('points.no_activity')}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t('points.points_shop')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-6">
              {rewards.map((reward) => {
                const Icon = reward.icon;
                const canAfford = (user?.points_balance || 0) >= reward.points;
                return (
                  <div 
                    key={reward.id} 
                    className={`p-6 rounded-lg border-2 transition-all ${
                      canAfford ? 'border-primary bg-primary/5 hover:shadow-lg' : 'border-gray-200 bg-gray-50 opacity-60'
                    }`}
                    data-testid={`reward-${reward.id}`}
                  >
                    <div className="flex justify-center mb-4">
                      <div className={`p-3 rounded-full ${canAfford ? 'bg-primary/20' : 'bg-gray-200'}`}>
                        <Icon className={`w-8 h-8 ${canAfford ? 'text-primary' : 'text-gray-400'}`} />
                      </div>
                    </div>
                    <h3 className="font-bold text-center mb-2">{t(reward.titleKey)}</h3>
                    <p className="text-sm text-gray-600 text-center mb-4">{t(reward.descKey)}</p>
                    <div className="text-center mb-4">
                      <span className="text-2xl font-bold text-primary">{reward.points}</span>
                      <span className="text-sm text-gray-600 ml-1">{t('common.points')}</span>
                    </div>
                    <Button 
                      className="w-full"
                      disabled={!canAfford}
                      onClick={() => handleRedeemReward(reward)}
                      data-testid={`redeem-${reward.id}`}
                    >
                      {canAfford ? t('points.redeem') : t('points.not_enough')}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default PointsPage;
